import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faExclamationCircle,
  faFloppyDisk,
  faInfoCircle,
  faTimes,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import './Toast.css'

export type ToastType = 'info' | 'error' | 'success' | 'save' | 'warning'

type ToastItem = {
  id: number
  message: string
  title?: string
  type: ToastType
}

type ToastContextValue = {
  push: (message: string, type?: ToastType, title?: string) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

function toneMeta(type: ToastType, isAr: boolean) {
  switch (type) {
    case 'success':
      return {
        icon: faCheckCircle,
        title: isAr ? 'تم بنجاح' : 'Erfolgreich',
        action: isAr ? 'موافق' : 'OK',
        cls: 'is-success',
      }
    case 'save':
      return {
        icon: faFloppyDisk,
        title: isAr ? 'تم الحفظ' : 'Gespeichert',
        action: isAr ? 'موافق' : 'OK',
        cls: 'is-save',
      }
    case 'error':
      return {
        icon: faExclamationCircle,
        title: isAr ? 'حدث خطأ' : 'Fehler',
        action: isAr ? 'فهمت' : 'Verstanden',
        cls: 'is-error',
      }
    case 'warning':
      return {
        icon: faTriangleExclamation,
        title: isAr ? 'تنبيه' : 'Hinweis',
        action: isAr ? 'فهمت' : 'Verstanden',
        cls: 'is-warning',
      }
    default:
      return {
        icon: faInfoCircle,
        title: isAr ? 'معلومة' : 'Info',
        action: isAr ? 'إغلاق' : 'Schließen',
        cls: 'is-info',
      }
  }
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation()
  const isAr = i18n.language?.startsWith('ar')
  const [items, setItems] = useState<ToastItem[]>([])

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const push = useCallback((message: string, type: ToastType = 'info', title?: string) => {
    const id = Date.now() + Math.floor(Math.random() * 1000)
    setItems((prev) => [...prev, { id, message, type, title }])
    // Soft auto-dismiss only for success/save/info — errors stay until confirmed
    if (type === 'success' || type === 'save' || type === 'info') {
      window.setTimeout(() => {
        setItems((prev) => prev.filter((t) => t.id !== id))
      }, 8000)
    }
  }, [])

  const value = useMemo(() => ({ push }), [push])
  const current = items[0]

  return (
    <ToastContext.Provider value={value}>
      {children}
      {current ? (
        <div className="alert-overlay" role="presentation" onClick={() => dismiss(current.id)}>
          <div
            className={`alert-modal ${toneMeta(current.type, Boolean(isAr)).cls}`}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={`alert-title-${current.id}`}
            aria-describedby={`alert-msg-${current.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="alert-x"
              aria-label={isAr ? 'إغلاق' : 'Schließen'}
              onClick={() => dismiss(current.id)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <div className="alert-icon" aria-hidden>
              <FontAwesomeIcon icon={toneMeta(current.type, Boolean(isAr)).icon} />
            </div>
            <h3 id={`alert-title-${current.id}`}>
              {current.title || toneMeta(current.type, Boolean(isAr)).title}
            </h3>
            <p id={`alert-msg-${current.id}`}>{current.message}</p>
            <button type="button" className="alert-action" onClick={() => dismiss(current.id)}>
              {toneMeta(current.type, Boolean(isAr)).action}
            </button>
          </div>
        </div>
      ) : null}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
